import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { EMPTY_SHIFTS_VIEW, type CashShiftStateFilter, type CashShiftsIntentState, type CashShiftsViewState } from './types'

/**
 * Отбор экрана «Смены 1С» по каждой базе: место, месяц, день, кассир и состояние. Сами смены — ответ службы — в кэше RTK Query;
 * отбор живёт до перезагрузки (`screen`).
 */
const initialState: CashShiftsIntentState = {}

/** Сколько свёрнутых узлов дерева помнит намерение. */
const MAX_COLLAPSED = 200

const viewOf = (state: CashShiftsIntentState, resourceId: string): CashShiftsViewState => {
  const rid = normalizeRid(resourceId)
  state[rid] ??= { ...EMPTY_SHIFTS_VIEW, collapsed: [] }
  return state[rid]
}

const shiftsSlice = createSlice({
  name: 'onec/shifts',
  initialState,
  reducers: {
    cashShiftsNodeChosen(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      viewOf(state, action.payload.resourceId).nodeId = action.payload.nodeId
    },
    cashShiftsTreeToggled(state, action: PayloadAction<{ resourceId: string; nodeId: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      const { nodeId } = action.payload
      view.collapsed = view.collapsed.includes(nodeId) ? view.collapsed.filter((id) => id !== nodeId) : [...view.collapsed, nodeId].slice(-MAX_COLLAPSED)
    },
    /** Другой месяц — другие смены: выбранный день прошлого месяца снимается. */
    cashShiftsMonthChanged(state, action: PayloadAction<{ resourceId: string; month: string }>) {
      const view = viewOf(state, action.payload.resourceId)
      view.month = action.payload.month
      view.day = null
    },
    cashShiftsDayChanged(state, action: PayloadAction<{ resourceId: string; day: string | null }>) {
      viewOf(state, action.payload.resourceId).day = action.payload.day
    },
    cashShiftsCashierChanged(state, action: PayloadAction<{ resourceId: string; cashier: string }>) {
      viewOf(state, action.payload.resourceId).cashier = action.payload.cashier
    },
    cashShiftsStateChanged(state, action: PayloadAction<{ resourceId: string; state: CashShiftStateFilter }>) {
      viewOf(state, action.payload.resourceId).state = action.payload.state
    },
  },
})

export const { cashShiftsNodeChosen, cashShiftsTreeToggled, cashShiftsMonthChanged, cashShiftsDayChanged, cashShiftsCashierChanged, cashShiftsStateChanged } =
  shiftsSlice.actions
export const cashShiftsReducer = shiftsSlice.reducer
